import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Box, Button, Checkbox, Dialog, DialogContent, DialogTitle, FormControlLabel, MenuItem, TextField, Typography } from "@mui/material";
import { Add } from "@mui/icons-material";
import { QRCodeCanvas } from "qrcode.react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { callPostApi } from "../../function/ApiCallFunction";
import { DataContext } from "../../context/DataContext";
import { consoleColors, primaryButtonSx } from "../admin/consoleTheme";
import { cashiLogo } from "../../include/cashiBrand";
import { indiaDate, indiaMinute } from "../../include/indiaDate";
import SpinPoster from "./SpinPoster";

const COUNTS = [3, 4, 5, 6, 8];
const COLORS = ["#21a7ec", "#f59e0b", "#16a34a", "#ef4444", "#7c3aed", "#ec4899", "#0d9488", "#f97316"];

function useSpinPaths() {
  const { pathname } = useLocation();
  const base = pathname.startsWith("/dashboardPlus") ? "/dashboardPlus/spinwin" : "/spinwin";
  return {
    list: base,
    create: `${base}/new`,
    edit: (id) => `${base}/${id}/edit`,
    wheel: (id) => `${base}/${id}/wheel`,
    winners: (id) => `${base}/${id}/winners`,
  };
}

function evenChances(count) {
  const base = Math.floor(100 / count);
  let left = 100 - base * count;
  return Array.from({ length: count }, () => {
    const extra = left > 0 ? 1 : 0;
    left -= extra;
    return base + extra;
  });
}

function blankOptions(count) {
  return evenChances(count).map((chance) => ({ title: "", chance, showQr: true }));
}

function toLocal(value) {
  return String(value || "").replace(" ", "T").slice(0, 16);
}

function defaultRange() {
  const start = indiaMinute().replace(" ", "T");
  const endDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
  return { start_at: start, end_at: indiaMinute(endDate).replace(" ", "T") };
}

function chanceTotal(options) {
  return options.reduce((sum, item) => sum + (Number.isInteger(Number(item.chance)) ? Number(item.chance) : 0), 0);
}

function drawWheel(canvas, options, rotation) {
  const ctx = canvas.getContext("2d");
  const size = 360;
  const ratio = canvas.width / size;
  ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
  ctx.clearRect(0, 0, size, size);
  const cx = size / 2;
  const cy = size / 2;
  const radius = 164;
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate((rotation * Math.PI) / 180);
  const count = Math.max(options.length, 1);
  const sweep = (Math.PI * 2) / count;
  let start = -Math.PI / 2;
  options.forEach((option, index) => {
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.arc(0, 0, radius, start, start + sweep);
    ctx.closePath();
    ctx.fillStyle = COLORS[index % COLORS.length];
    ctx.fill();
    ctx.lineWidth = 3;
    ctx.strokeStyle = "#ffffff";
    ctx.stroke();
    ctx.save();
    ctx.rotate(start + sweep / 2);
    ctx.fillStyle = "#ffffff";
    ctx.textAlign = "right";
    ctx.textBaseline = "middle";
    ctx.font = count > 6 ? "700 12px Manrope, sans-serif" : "700 15px Manrope, sans-serif";
    ctx.fillText(String(option.title || `Option ${index + 1}`).slice(0, count > 6 ? 12 : 16), radius - 16, 0);
    ctx.restore();
    start += sweep;
  });
  ctx.restore();
  ctx.beginPath();
  ctx.arc(cx, cy, 34, 0, Math.PI * 2);
  ctx.fillStyle = "#0a0a0a";
  ctx.fill();
  ctx.beginPath();
  ctx.arc(cx, cy, 12, 0, Math.PI * 2);
  ctx.fillStyle = "#ffffff";
  ctx.fill();
}

function landingRotation(options, index, from) {
  const count = Math.max(options.length, 1);
  const slice = 360 / count;
  const center = index * slice + slice / 2;
  const rest = (360 - center) % 360;
  const base = Math.ceil(from / 360) * 360;
  let next = base + 5 * 360 + rest;
  if (next <= from + 360) next += 360;
  return next;
}

export default function SpinWheelList() {
  const navigate = useNavigate();
  const paths = useSpinPaths();
  const { loginData } = useContext(DataContext);
  const storeId = loginData?.store_info?.store_id;
  const [wheels, setWheels] = useState([]);

  const load = useCallback(async () => {
    if (!storeId) return;
    const result = await callPostApi("store-spin", { mod: "SPIN_LIST", data_arr: { store_id: storeId } });
    if (result.status === "200") setWheels(result.data?.wheels || []);
    else toast.error(result?.status_message || "Could not load wheels");
  }, [storeId]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (wheel) => {
    const result = await callPostApi("store-spin", {
      mod: "SPIN_STATUS",
      data_arr: { store_id: storeId, wheel_id: wheel.wheel_id, status: wheel.status === "1" ? "0" : "1" },
    });
    if (result.status === "200") {
      toast.success(result.data?.message || "Updated");
      load();
    } else toast.error(result?.status_message || "Could not update");
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
        <Typography sx={{ color: consoleColors.muted, fontSize: 14 }}>Copy the tablet link and open it on the store TV. The customer spins there.</Typography>
        <Button variant="contained" sx={primaryButtonSx} onClick={() => navigate(paths.create)}><Add sx={{ mr: 0.5 }} /> Create Spin To Win</Button>
      </Box>
      {wheels.length === 0 && (
        <Box sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2, p: 4, textAlign: "center", color: consoleColors.muted }}>
          No spin wheels yet.
        </Box>
      )}
      <Box sx={{ display: "grid", gap: 1.5 }}>
        {wheels.map((wheel) => (
          <Box key={wheel.wheel_id} sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2.5, p: 2, display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
            <Box sx={{ flex: 1, minWidth: 220 }}>
              <Typography sx={{ fontSize: 18, fontWeight: 700 }}>{wheel.headline}</Typography>
              <Typography sx={{ fontSize: 13, color: consoleColors.muted, mt: 0.4 }}>
                {wheel.tag ? `${wheel.tag} · ` : ""}{wheel.option_count} options · {indiaDate(wheel.start_at)} to {indiaDate(wheel.end_at)}
              </Typography>
              <Typography sx={{ fontSize: 13, mt: 0.4 }}>
                {wheel.claim_count || 0} spins · {wheel.status === "1" ? "Enabled" : "Disabled"}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button size="small" sx={{ textTransform: "none" }} onClick={() => navigate(paths.edit(wheel.wheel_id))}>Edit</Button>
              <Button size="small" sx={{ textTransform: "none" }} onClick={() => toggle(wheel)}>{wheel.status === "1" ? "Disable" : "Enable"}</Button>
              <Button size="small" sx={{ textTransform: "none" }} onClick={() => navigate(`/spinwheel/${wheel.screen_token}`)}>Show wheel</Button>
              <Button size="small" sx={{ textTransform: "none" }} onClick={() => {
                const link = `${window.location.origin}/spinwheel/${wheel.screen_token}`;
                navigator.clipboard.writeText(link).then(() => toast.success("Tablet link copied")).catch(() => toast.info(link));
              }}>Tablet link</Button>
              <Button size="small" sx={{ textTransform: "none" }} onClick={() => navigate(paths.winners(wheel.wheel_id))}>Prize list</Button>
              <SpinPoster wheel={wheel} storeName={loginData?.store_info?.store_name} storeImage={loginData?.store_info?.store_image} />
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export function SpinWheelForm() {
  const { wheelId } = useParams();
  const navigate = useNavigate();
  const paths = useSpinPaths();
  const { loginData } = useContext(DataContext);
  const storeId = loginData?.store_info?.store_id;
  const range = defaultRange();
  const [headline, setHeadline] = useState("");
  const [tag, setTag] = useState("");
  const [startAt, setStartAt] = useState(range.start_at);
  const [endAt, setEndAt] = useState(range.end_at);
  const [count, setCount] = useState(4);
  const [options, setOptions] = useState(blankOptions(4));
  const [saving, setSaving] = useState(false);
  const total = chanceTotal(options);

  useEffect(() => {
    if (!wheelId || !storeId) return;
    let mounted = true;
    callPostApi("store-spin", { mod: "SPIN_DETAIL", data_arr: { store_id: storeId, wheel_id: wheelId } }).then((result) => {
      if (!mounted || result.status !== "200") return;
      const wheel = result.data?.wheel || {};
      const wheelQr = wheel.show_qr !== "0";
      const next = (wheel.options || []).map((item) => ({
        title: item.title || "",
        chance: item.chance ?? "",
        showQr: item.show_qr === "0" ? false : item.show_qr === "1" ? true : wheelQr,
      }));
      setHeadline(wheel.headline || "");
      setTag(wheel.tag || "");
      setStartAt(toLocal(wheel.start_at));
      setEndAt(toLocal(wheel.end_at));
      setCount(next.length || 4);
      setOptions(next.length ? next : blankOptions(4));
    });
    return () => { mounted = false; };
  }, [wheelId, storeId]);

  const changeCount = (value) => {
    const nextCount = Number(value);
    const chances = evenChances(nextCount);
    setCount(nextCount);
    setOptions((prev) => chances.map((chance, index) => ({ title: prev[index]?.title || "", chance, showQr: prev[index]?.showQr !== false })));
  };

  const setOption = (index, key, value) => {
    setOptions((prev) => prev.map((item, itemIndex) => (itemIndex === index ? { ...item, [key]: value } : item)));
  };

  const save = async () => {
    setSaving(true);
    try {
      const result = await callPostApi("store-spin", {
        mod: "SPIN_SAVE",
        data_arr: {
          store_id: storeId,
          wheel_id: wheelId || "",
          headline,
          tag,
          start_at: startAt,
          end_at: endAt,
          option_count: count,
          options: options.map((item) => ({ title: item.title, chance: Number(item.chance), show_qr: item.showQr === false ? "0" : "1" })),
        },
      });
      if (result.status === "200") {
        toast.success(result.data?.message || "Saved");
        navigate(paths.list);
      } else toast.error(result?.status_message || result?.data?.error || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 860 }}>
      <Box sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2.5, p: { xs: 2, md: 3 }, display: "grid", gap: 1.5 }}>
        <TextField label="Top line" value={headline} onChange={(event) => setHeadline(event.target.value)} helperText="Shown above the wheel" />
        <TextField label="Tag" value={tag} onChange={(event) => setTag(event.target.value)} helperText="A short remark for your use" />
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 160px" }, gap: 1.4 }}>
          <TextField label="Start date" type="datetime-local" value={startAt} onChange={(event) => setStartAt(event.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField label="End date" type="datetime-local" value={endAt} onChange={(event) => setEndAt(event.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField select label="Number of options" value={count} onChange={(event) => changeCount(event.target.value)}>
            {COUNTS.map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}
          </TextField>
        </Box>
        <Typography sx={{ fontWeight: 700, fontSize: 15 }}>Options</Typography>
        <Typography sx={{ fontSize: 13, color: consoleColors.muted, mt: -0.8 }}>Show QR is per option. When that option wins, the QR asks for name and mobile. Leave it off and they can spin again.</Typography>
        {options.map((option, index) => (
          <Box key={index} sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 120px auto" }, gap: 1.2, alignItems: "center" }}>
            <TextField label={`Option ${index + 1} title`} value={option.title} onChange={(event) => setOption(index, "title", event.target.value)} />
            <TextField label="Chance %" type="number" value={option.chance} onChange={(event) => setOption(index, "chance", event.target.value)} />
            <FormControlLabel
              sx={{ m: 0 }}
              control={<Checkbox checked={option.showQr !== false} onChange={(event) => setOption(index, "showQr", event.target.checked)} />}
              label="Show QR"
            />
          </Box>
        ))}
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: total === 100 ? "#15803d" : "#b45309" }}>
          Total chance {total}%{total === 100 ? "" : " — must be 100%"}
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button sx={{ textTransform: "none" }} onClick={() => navigate(paths.list)}>Back</Button>
          <Button variant="contained" sx={primaryButtonSx} disabled={saving || total !== 100} onClick={save}>{saving ? "Saving..." : "Save"}</Button>
        </Box>
      </Box>
    </Box>
  );
}

export function SpinWheelPlay() {
  const { wheelId } = useParams();
  const navigate = useNavigate();
  const { loginData } = useContext(DataContext);
  const storeId = loginData?.store_info?.store_id;

  useEffect(() => {
    if (!wheelId || !storeId) return undefined;
    let mounted = true;
    callPostApi("store-spin", { mod: "SPIN_DETAIL", data_arr: { store_id: storeId, wheel_id: wheelId } }).then((result) => {
      if (!mounted) return;
      const token = result.data?.wheel?.screen_token;
      if (result.status === "200" && token) navigate(`/spinwheel/${token}`, { replace: true });
      else toast.error(result?.status_message || "Could not open this wheel");
    });
    return () => { mounted = false; };
  }, [wheelId, storeId, navigate]);

  return null;
}

export function SpinWheelScreen() {
  const { token } = useParams();
  const canvasRef = useRef(null);
  const rotationRef = useRef(0);
  const spinningRef = useRef(false);
  const [wheel, setWheel] = useState(null);
  const [rotation, setRotation] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [win, setWin] = useState(null);
  const [qrOpen, setQrOpen] = useState(false);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    if (!token) return undefined;
    let mounted = true;
    const load = () => {
      if (spinningRef.current) return;
      callPostApi("spin-screen", { mod: "SPIN_SCREEN", data_arr: { token } }).then((result) => {
        if (!mounted || spinningRef.current) return;
        if (result.status === "200") setWheel(result.data?.wheel || null);
        else setMissing(true);
      });
    };
    load();
    const timer = setInterval(load, 2000);
    return () => { mounted = false; clearInterval(timer); };
  }, [token]);

  const waiting = Boolean(wheel) && wheel.ready !== "1" && !spinning && !qrOpen;
  useEffect(() => {
    if (!waiting) return undefined;
    let frame = 0;
    let last = performance.now();
    const tick = (now) => {
      const step = (now - last) / 1000;
      last = now;
      rotationRef.current += step * 0.45;
      setRotation(rotationRef.current);
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [waiting]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !wheel?.options?.length) return;
    const ratio = window.devicePixelRatio || 1;
    canvas.width = 360 * ratio;
    canvas.height = 360 * ratio;
    drawWheel(canvas, wheel.options, rotation);
  }, [wheel, rotation]);

  const spin = async () => {
    if (!wheel || spinning || wheel.closed || wheel.ready !== "1") return;
    spinningRef.current = true;
    setSpinning(true);
    setWin(null);
    setQrOpen(false);
    try {
      const result = await callPostApi("spin-screen", { mod: "SPIN_SCREEN_PLAY", data_arr: { token } });
      if (result.status !== "200") {
        toast.error(result?.status_message || "Could not spin");
        spinningRef.current = false;
        setSpinning(false);
        return;
      }
      const target = landingRotation(wheel.options, result.data.option_index, rotationRef.current);
      const from = rotationRef.current;
      const started = performance.now();
      const duration = 4800;
      const step = (now) => {
        const t = Math.min(1, (now - started) / duration);
        const eased = 1 - (1 - t) ** 3;
        const next = from + (target - from) * eased;
        rotationRef.current = next;
        setRotation(next);
        if (t < 1) requestAnimationFrame(step);
        else {
          setWin(result.data);
          setQrOpen(true);
          spinningRef.current = false;
          setSpinning(false);
        }
      };
      requestAnimationFrame(step);
    } catch {
      spinningRef.current = false;
      setSpinning(false);
      toast.error("Could not spin");
    }
  };

  const claimLink = win?.claim_id ? `${window.location.origin}/spin/${win.claim_id}` : "";
  const filledWin = win?.filled === "1";
  const closeWin = useCallback(() => {
    setQrOpen(false);
    setWheel((current) => (current ? { ...current, ready: "0" } : current));
  }, []);

  useEffect(() => {
    if (!qrOpen || !win?.claim_id || filledWin) return undefined;
    let mounted = true;
    const load = () => {
      callPostApi("spin-screen", { mod: "SPIN_SCREEN_CLAIM_STATUS", data_arr: { token, claim_id: win.claim_id } }).then((result) => {
        if (!mounted || result.status !== "200" || result.data?.filled !== "1") return;
        setWin((current) => (current ? {
          ...current,
          filled: "1",
          customer_name: result.data.customer_name,
          customer_mobile: result.data.customer_mobile,
        } : current));
      });
    };
    load();
    const timer = setInterval(load, 1500);
    return () => { mounted = false; clearInterval(timer); };
  }, [qrOpen, win?.claim_id, filledWin, token]);

  useEffect(() => {
    if (!qrOpen || !filledWin || !win?.claim_id) return undefined;
    let cancelled = false;
    callPostApi("spin-screen", { mod: "SPIN_SCREEN_CLAIM", data_arr: { token, claim_id: win.claim_id } }).then((result) => {
      if (cancelled) return;
      if (result.status !== "200") toast.error(result?.status_message || "Could not claim this prize");
    }).catch(() => {
      if (!cancelled) toast.error("Could not claim this prize");
    });
    const timer = setTimeout(() => {
      if (!cancelled) closeWin();
    }, 5000);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [qrOpen, filledWin, win?.claim_id, token, closeWin]);

  return (
    <Box sx={{
      minHeight: "100vh",
      position: "relative",
      overflow: "hidden",
      display: "grid",
      justifyItems: "center",
      alignContent: "center",
      gap: 2.5,
      px: 2,
      py: 4,
      background: waiting
        ? "linear-gradient(125deg, #1d4ed8, #7c3aed, #db2777, #f59e0b, #21a7ec)"
        : "#0b1220",
      backgroundSize: waiting ? "300% 300%" : "auto",
      animation: waiting ? "wait-bg 6s ease-in-out infinite" : "none",
    }}>
      {waiting && <WaitBackdrop />}
      {missing && <Typography sx={{ fontSize: 22, fontWeight: 750, color: "#fff" }}>This wheel link is not valid.</Typography>}
      {wheel && (
        <>
          <Box sx={{ textAlign: "center", maxWidth: 720, position: "relative", zIndex: 1 }}>
            <Typography sx={{ fontSize: { xs: 32, md: 48 }, fontWeight: 800, color: "#fff", textShadow: "0 4px 18px rgba(0,0,0,0.28)" }}>{wheel.headline || "Spin To Win"}</Typography>
            {wheel.tag && <Typography sx={{ color: "rgba(255,255,255,0.82)", mt: 0.6, fontSize: 18 }}>{wheel.tag}</Typography>}
            {wheel.closed && <Typography sx={{ color: "#fde68a", mt: 1, fontWeight: 700 }}>{wheel.closed}</Typography>}
            {waiting && <WaitParty lastPrize={wheel.last_prize} lastName={wheel.last_name} />}
            {!wheel.closed && wheel.ready === "1" && !qrOpen && !spinning && (
              <Typography sx={{ color: "#86efac", mt: 1, fontWeight: 800, fontSize: 22, animation: "wait-pop 1.1s ease-in-out infinite" }}>Your turn. Tap Spin</Typography>
            )}
            {!qrOpen && !waiting && !spinning && wheel.ready === "1" && wheel.last_prize && (
              <Typography sx={{ color: "rgba(255,255,255,0.9)", mt: 0.8, fontSize: 16 }}>
                Last win {wheel.last_prize}{wheel.last_name ? ` · ${wheel.last_name}` : ""}
              </Typography>
            )}
          </Box>
          <Box sx={{ position: "relative", width: "min(640px, 92vw)", zIndex: 1, animation: waiting ? "wait-glow 2.4s ease-in-out infinite" : spinning ? "spin-live 0.9s ease-in-out infinite" : "none" }}>
            {waiting && <WaitSparkles />}
            {waiting && <WaitRings />}
            <Box sx={{ position: "absolute", left: "50%", top: -8, transform: "translateX(-50%)", width: 0, height: 0, borderLeft: "16px solid transparent", borderRight: "16px solid transparent", borderTop: "28px solid #111", zIndex: 2 }} />
            <canvas ref={canvasRef} style={{ width: "100%", height: "auto", display: "block" }} />
            {!qrOpen && wheel.ready === "1" && (
              <Button
                onClick={spin}
                disabled={spinning || Boolean(wheel.closed)}
                sx={{
                  ...primaryButtonSx,
                  position: "absolute",
                  left: "50%",
                  top: "50%",
                  transform: "translate(-50%, -50%)",
                  zIndex: 3,
                  width: "min(124px, 30%)",
                  aspectRatio: "1",
                  minWidth: 0,
                  height: "auto",
                  borderRadius: "50%",
                  fontSize: 20,
                  fontWeight: 800,
                  border: "6px solid #fff",
                }}
              >
                {spinning ? "..." : "Spin"}
              </Button>
            )}
            {qrOpen && win && (
              <Box sx={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%, -50%)", zIndex: 4, width: "min(420px, 88%)" }}>
                <WinBlast />
                <Box sx={{
                  position: "relative",
                  background: "#fff",
                  borderRadius: 4,
                  overflow: "hidden",
                  textAlign: "center",
                  boxShadow: "0 22px 48px rgba(0,0,0,0.38)",
                  animation: "spin-card-pop 0.55s cubic-bezier(.2,1.3,.4,1)",
                  border: "3px solid #f59e0b",
                }}>
                  <Box sx={{ background: "linear-gradient(110deg, #0a0a0a, #7c3aed, #db2777)", px: 1.5, pt: 1.2, pb: 1.1, position: "relative" }}>
                    <Button onClick={closeWin} sx={{ position: "absolute", top: 6, right: 6, minWidth: 0, px: 1.2, py: 0.3, textTransform: "none", color: "#fff", fontWeight: 800, zIndex: 2, background: "rgba(255,255,255,0.12)", borderRadius: 2 }}>Close</Button>
                    <Box component="img" src={cashiLogo} alt="Cashi" sx={{ height: 30, width: "auto", display: "block", mx: "auto" }} />
                  </Box>
                  <Box sx={{ p: { xs: 1.8, sm: 2.2 }, background: "linear-gradient(180deg, #fff7ed 0%, #ffffff 42%)" }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 800, letterSpacing: 1.4, color: "#ea580c", textTransform: "uppercase" }}>You won</Typography>
                    <Typography sx={{ fontSize: { xs: 24, sm: 28 }, fontWeight: 900, color: "#15803d", mt: 0.3, lineHeight: 1.15 }}>{win.prize_title}</Typography>
                    {filledWin ? (
                      <>
                        <Typography sx={{ fontSize: { xs: 16, sm: 18 }, fontWeight: 800, color: "#0f172a", mt: 1.4 }}>{win.customer_name}</Typography>
                        <Typography sx={{ fontSize: { xs: 15, sm: 16 }, fontWeight: 700, color: "#334155", mt: 0.25 }}>{win.customer_mobile}</Typography>
                        <Typography sx={{ fontSize: { xs: 15, sm: 16 }, fontWeight: 700, color: "#7c3aed", mt: 1.1, mb: 0.4 }}>won this prize</Typography>
                      </>
                    ) : (
                      <>
                        <Typography sx={{ fontSize: { xs: 16, sm: 18 }, fontWeight: 800, color: "#0f172a", mt: 1.2, lineHeight: 1.3 }}>
                          Scan this QR
                        </Typography>
                        <Typography sx={{ fontSize: { xs: 14, sm: 15 }, fontWeight: 650, color: "#334155", mt: 0.35, mb: 1.3, lineHeight: 1.4 }}>
                          Then enter your name and mobile to claim the prize.
                        </Typography>
                        <Box sx={{
                          display: "inline-flex",
                          flexDirection: "column",
                          alignItems: "center",
                          background: "#fff",
                          borderRadius: 3,
                          p: 1.2,
                          border: "3px solid #21a7ec",
                          boxShadow: "0 0 0 6px rgba(33,167,236,0.16)",
                          animation: "spin-qr-pulse 1.6s ease-in-out infinite",
                        }}>
                          <Typography sx={{ fontSize: 12, fontWeight: 900, letterSpacing: 1.6, color: "#21a7ec", mb: 0.6 }}>SCAN ME</Typography>
                          <QRCodeCanvas value={claimLink} size={220} />
                        </Box>
                      </>
                    )}
                  </Box>
                </Box>
              </Box>
            )}
            <style>{`
              @keyframes spin-card-pop {
                0% { transform: scale(0.35); opacity: 0; }
                65% { transform: scale(1.08); opacity: 1; }
                100% { transform: scale(1); opacity: 1; }
              }
              @keyframes spin-blast {
                0% { transform: translate(-50%, -50%) scale(0.2); opacity: 1; }
                70% { opacity: 1; }
                100% { transform: translate(calc(-50% + var(--bx)), calc(-50% + var(--by))) scale(1); opacity: 0; }
              }
              @keyframes spin-ring {
                0% { transform: translate(-50%, -50%) scale(0.15); opacity: 0.85; }
                100% { transform: translate(-50%, -50%) scale(2.6); opacity: 0; }
              }
              @keyframes wait-fall {
                0% { transform: translateY(-18px) rotate(0deg); opacity: 0; }
                10% { opacity: 1; }
                100% { transform: translateY(110vh) rotate(360deg); opacity: 0; }
              }
              @keyframes wait-glow {
                0%, 100% { filter: drop-shadow(0 0 8px rgba(255,255,255,0.2)); }
                50% { filter: drop-shadow(0 0 28px rgba(250,204,21,0.85)) drop-shadow(0 0 42px rgba(236,72,153,0.55)); }
              }
              @keyframes wait-title {
                0% { background-position: 0% 50%; }
                100% { background-position: 200% 50%; }
              }
              @keyframes wait-spin {
                to { transform: rotate(360deg); }
              }
              @keyframes wait-bg {
                0% { background-position: 0% 40%; }
                50% { background-position: 100% 60%; }
                100% { background-position: 0% 40%; }
              }
              @keyframes wait-pop {
                0%, 100% { transform: scale(1); }
                50% { transform: scale(1.08); }
              }
              @keyframes wait-ring {
                0% { transform: translate(-50%, -50%) scale(0.72); opacity: 0.7; }
                100% { transform: translate(-50%, -50%) scale(1.35); opacity: 0; }
              }
              @keyframes spin-live {
                0%, 100% { filter: drop-shadow(0 0 10px rgba(33,167,236,0.35)); }
                50% { filter: drop-shadow(0 0 22px rgba(245,158,11,0.7)); }
              }
              @keyframes spin-qr-pulse {
                0%, 100% { box-shadow: 0 0 0 6px rgba(33,167,236,0.14); }
                50% { box-shadow: 0 0 0 12px rgba(245,158,11,0.28); }
              }
            `}</style>
          </Box>
        </>
      )}
    </Box>
  );
}

function WaitParty({ lastPrize, lastName }) {
  return (
    <Box sx={{
      position: "relative",
      mt: 1.6,
      overflow: "hidden",
      borderRadius: 4,
      px: 2.2,
      py: 2,
      background: "linear-gradient(110deg, rgba(255,255,255,0.18), rgba(250,204,21,0.22), rgba(236,72,153,0.22), rgba(33,167,236,0.22))",
      border: "1px solid rgba(255,255,255,0.28)",
      boxShadow: "0 12px 32px rgba(0,0,0,0.18)",
      animation: "wait-pop 2.2s ease-in-out infinite",
    }}>
      <Typography sx={{
        position: "relative",
        fontWeight: 900,
        fontSize: { xs: 22, md: 30 },
        letterSpacing: 0.4,
        background: "linear-gradient(90deg, #fff, #fde68a, #fb7185, #67e8f9, #c4b5fd, #fff)",
        backgroundSize: "220% 100%",
        WebkitBackgroundClip: "text",
        backgroundClip: "text",
        color: "transparent",
        animation: "wait-title 2.2s linear infinite",
      }}>
        Waiting for the next spin
      </Typography>
      {lastPrize && (
        <Typography sx={{ position: "relative", mt: 0.6, fontWeight: 750, color: "#fef08a", fontSize: 16 }}>
          Last win {lastPrize}{lastName ? ` · ${lastName}` : ""}
        </Typography>
      )}
    </Box>
  );
}

function WaitBackdrop() {
  const bits = Array.from({ length: 42 }, (_, index) => ({
    left: `${(index * 17) % 100}%`,
    delay: `${(index % 10) * 0.22}s`,
    duration: `${4.2 + (index % 6) * 0.45}s`,
    color: COLORS[index % COLORS.length],
    size: 8 + (index % 5) * 4,
    round: index % 3 !== 1,
  }));
  return (
    <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden" }}>
      {bits.map((bit, index) => (
        <Box
          key={index}
          sx={{
            position: "absolute",
            left: bit.left,
            top: -24,
            width: bit.size,
            height: bit.round ? bit.size : bit.size + 8,
            borderRadius: bit.round ? "50%" : "2px",
            background: bit.color,
            boxShadow: `0 0 10px ${bit.color}`,
            animation: `wait-fall ${bit.duration} linear ${bit.delay} infinite`,
          }}
        />
      ))}
    </Box>
  );
}

function WaitRings() {
  return (
    <Box sx={{ position: "absolute", left: "50%", top: "50%", width: 0, height: 0, pointerEvents: "none", zIndex: 0 }}>
      {["#21a7ec", "#f59e0b", "#ec4899"].map((color, index) => (
        <Box
          key={color}
          sx={{
            position: "absolute",
            width: { xs: 220, md: 280 },
            height: { xs: 220, md: 280 },
            borderRadius: "50%",
            border: `3px solid ${color}`,
            animation: `wait-ring ${2 + index * 0.4}s ease-out ${index * 0.35}s infinite`,
          }}
        />
      ))}
    </Box>
  );
}

function WaitSparkles() {
  const dots = Array.from({ length: 16 }, (_, index) => {
    const angle = (index / 16) * Math.PI * 2;
    return {
      color: COLORS[index % COLORS.length],
      left: `${50 + Math.cos(angle) * 51}%`,
      top: `${50 + Math.sin(angle) * 51}%`,
      size: 10 + (index % 3) * 5,
      round: index % 2 === 0,
    };
  });
  return (
    <Box sx={{ position: "absolute", inset: "-8%", pointerEvents: "none", zIndex: 1, animation: "wait-spin 7s linear infinite" }}>
      {dots.map((dot, index) => (
        <Box
          key={index}
          sx={{
            position: "absolute",
            left: dot.left,
            top: dot.top,
            width: dot.size,
            height: dot.round ? dot.size : dot.size + 5,
            borderRadius: dot.round ? "50%" : "3px",
            background: dot.color,
            boxShadow: `0 0 16px ${dot.color}`,
            transform: "translate(-50%, -50%)",
          }}
        />
      ))}
    </Box>
  );
}

function WinBlast() {
  const pieces = Array.from({ length: 32 }, (_, index) => {
    const angle = (index / 32) * Math.PI * 2;
    const distance = 110 + (index % 5) * 26;
    return {
      color: COLORS[index % COLORS.length],
      x: Math.round(Math.cos(angle) * distance),
      y: Math.round(Math.sin(angle) * distance),
      delay: (index % 6) * 0.03,
      size: index % 3 === 0 ? 11 : 7,
      round: index % 2 === 0,
    };
  });
  return (
    <Box sx={{ position: "absolute", left: "50%", top: "50%", width: 0, height: 0, pointerEvents: "none", zIndex: 0 }}>
      {[0, 1].map((ring) => (
        <Box key={ring} sx={{ position: "absolute", width: 80, height: 80, borderRadius: "50%", border: "3px solid #21a7ec", animation: `spin-ring 0.85s ease-out ${ring * 0.12}s both` }} />
      ))}
      {pieces.map((piece, index) => (
        <Box
          key={index}
          sx={{
            position: "absolute",
            width: piece.size,
            height: piece.round ? piece.size : piece.size + 4,
            borderRadius: piece.round ? "50%" : "2px",
            background: piece.color,
            "--bx": `${piece.x}px`,
            "--by": `${piece.y}px`,
            animation: `spin-blast 0.95s cubic-bezier(.15,.8,.3,1) ${piece.delay}s both`,
          }}
        />
      ))}
    </Box>
  );
}

export function SpinWinners() {
  const { wheelId } = useParams();
  const navigate = useNavigate();
  const paths = useSpinPaths();
  const { loginData } = useContext(DataContext);
  const storeId = loginData?.store_info?.store_id;
  const [headline, setHeadline] = useState("");
  const [rows, setRows] = useState([]);
  const [daily, setDaily] = useState([]);
  const [giveRow, setGiveRow] = useState(null);
  const [giveForm, setGiveForm] = useState({ customer_name: "", customer_mobile: "", customer_email: "" });
  const [giving, setGiving] = useState(false);

  const load = useCallback(async () => {
    if (!storeId || !wheelId) return;
    const result = await callPostApi("store-spin", { mod: "SPIN_WINNERS", data_arr: { store_id: storeId, wheel_id: wheelId } });
    if (result.status === "200") {
      setHeadline(result.data?.headline || "");
      setRows(result.data?.winners || []);
      setDaily(result.data?.daily || []);
    } else toast.error(result?.status_message || "Could not load prizes");
  }, [storeId, wheelId]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (row) => {
    if (row.given === "1") {
      const result = await callPostApi("store-spin", { mod: "SPIN_GIVEN", data_arr: { store_id: storeId, claim_id: row.claim_id } });
      if (result.status === "200") {
        toast.success(result.data?.message || "Updated");
        load();
      } else toast.error(result?.status_message || "Could not update");
      return;
    }
    setGiveForm({
      customer_name: row.customer_name || "",
      customer_mobile: row.customer_mobile || "",
      customer_email: row.customer_email || "",
    });
    setGiveRow(row);
  };

  const givePrize = async () => {
    if (!giveRow || giving) return;
    setGiving(true);
    try {
      const result = await callPostApi("store-spin", {
        mod: "SPIN_GIVEN",
        data_arr: {
          store_id: storeId,
          claim_id: giveRow.claim_id,
          customer_name: giveForm.customer_name,
          customer_mobile: giveForm.customer_mobile,
          customer_email: giveForm.customer_email,
        },
      });
      if (result.status === "200") {
        toast.success(result.data?.message || "Prize given");
        setGiveRow(null);
        load();
      } else toast.error(result?.status_message || "Could not give the prize");
    } finally {
      setGiving(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
        <Typography sx={{ fontWeight: 750 }}>{headline}</Typography>
        <Button sx={{ textTransform: "none" }} onClick={() => navigate(paths.list)}>Back</Button>
      </Box>
      {daily.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 750, mb: 0.8 }}>Number of spins daily</Typography>
          <Box sx={{ display: "grid", gap: 0.6 }}>
            {daily.map((item) => (
              <Box key={item.date} sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2, px: 1.5, py: 1, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
                <Typography sx={{ fontSize: 14 }}>{indiaDate(item.date)}</Typography>
                <Typography sx={{ fontWeight: 750 }}>{item.count}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
      {rows.length === 0 && (
        <Box sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2, p: 4, textAlign: "center", color: consoleColors.muted }}>
          No prizes yet. Spin the wheel first.
        </Box>
      )}
      <Box sx={{ display: "grid", gap: 1 }}>
        {rows.map((row) => (
          <Box key={row.claim_id} sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2, p: 1.5, display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.2fr 1fr 1.2fr auto" }, gap: 1, alignItems: "center" }}>
            <Box>
              <Typography sx={{ fontSize: 12, color: consoleColors.muted }}>Name</Typography>
              <Typography sx={{ fontWeight: 700 }}>{row.customer_name || "Waiting for customer"}</Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: 12, color: consoleColors.muted }}>Mobile</Typography>
              <Typography>{row.customer_mobile || "—"}</Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: 12, color: consoleColors.muted }}>Prize</Typography>
              <Typography sx={{ fontWeight: 700 }}>{row.prize_title}</Typography>
            </Box>
            <Button
              variant={row.given === "1" ? "contained" : "outlined"}
              sx={{ textTransform: "none", ...(row.given === "1" ? { background: "#15803d", "&:hover": { background: "#166534" } } : {}) }}
              onClick={() => toggle(row)}
            >
              {row.given === "1" ? "Given" : "Not given"}
            </Button>
          </Box>
        ))}
      </Box>
      <Dialog open={Boolean(giveRow)} onClose={() => setGiveRow(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 750 }}>Give prize</DialogTitle>
        <DialogContent sx={{ display: "grid", gap: 1.4, pt: "8px !important" }}>
          <Typography sx={{ fontWeight: 800, color: "#15803d" }}>{giveRow?.prize_title}</Typography>
          <Typography sx={{ color: consoleColors.muted, fontSize: 14 }}>Enter name and mobile to give this prize.</Typography>
          <TextField label="Name" value={giveForm.customer_name} onChange={(event) => setGiveForm((prev) => ({ ...prev, customer_name: event.target.value }))} />
          <TextField label="Mobile" value={giveForm.customer_mobile} onChange={(event) => setGiveForm((prev) => ({ ...prev, customer_mobile: event.target.value.replace(/\D/g, "").slice(0, 15) }))} inputProps={{ inputMode: "numeric" }} />
          <TextField label="Email" value={giveForm.customer_email} onChange={(event) => setGiveForm((prev) => ({ ...prev, customer_email: event.target.value }))} helperText="Optional" />
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 0.5 }}>
            <Button sx={{ textTransform: "none" }} onClick={() => setGiveRow(null)}>Cancel</Button>
            <Button variant="contained" sx={primaryButtonSx} disabled={giving} onClick={givePrize}>{giving ? "Saving..." : "Give prize"}</Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
